import type Phaser from 'phaser';
import type { BattleEvent, BattleSnapshot, DebugSnapshot } from '../../core';
import { BattleEventType } from '../../core';
import type { StickViewModel } from '../battle/stick-figure-model';
import { SKILL_COLORS } from './skill-presentation-model';

interface ActiveSlowStatus {
  readonly player: 0 | 1;
  readonly effectId: string;
}

/**
 * SkillStatusRenderer
 *
 * Renders persistent skill state visuals strictly synchronized with Core Source of Truth:
 * - Developer Counter Window: synchronized with Developer pose/attackPhase in BattleSnapshot.
 * - Developer Spaghetti Code Trap: synchronized frame-by-frame with Core debug.boxes (type === 'TRAP').
 * - Developer SLOW debuff: synchronized directly to Core EFFECT_APPLIED / EFFECT_EXPIRED events with ZERO presentation timers.
 *
 * Note: Sales TIME_PENALTY is an instant Core effect (timerFrames deduction) and does not maintain a persistent status.
 */
export class SkillStatusRenderer {
  private activeSlowStatuses: ActiveSlowStatus[] = [];

  public updateEvents(events: readonly BattleEvent[]): void {
    for (const event of events) {
      if (event.type === BattleEventType.EFFECT_APPLIED) {
        const effectType = String(event.payload.effectType ?? '');
        if (effectType === 'SLOW') {
          const target = Number(event.payload.target);
          const effectId = String(event.payload.effectId ?? '');
          if (target === 0 || target === 1) {
            this.activeSlowStatuses = this.activeSlowStatuses.filter(
              (s) => !(s.player === target && s.effectId === effectId),
            );
            this.activeSlowStatuses.push({
              player: target,
              effectId,
            });
          }
        }
      } else if (event.type === BattleEventType.EFFECT_EXPIRED) {
        const effectType = String(event.payload.effectType ?? '');
        if (effectType === 'SLOW') {
          const player = Number(event.payload.player);
          const effectId = String(event.payload.effectId ?? '');
          if (player === 0 || player === 1) {
            this.activeSlowStatuses = this.activeSlowStatuses.filter(
              (s) => !(s.player === player && s.effectId === effectId),
            );
          }
        }
      }
    }
  }

  public stepFrame(): void {
    // Zero presentation-side lifetime decrement.
    // Display lifecycle is 100% driven by Core Source of Truth (BattleSnapshot, DebugSnapshot, and Core Events).
  }

  public render(
    graphics: Phaser.GameObjects.Graphics,
    snapshot: BattleSnapshot,
    debug: DebugSnapshot,
    views: readonly [StickViewModel, StickViewModel],
    nowMs: number,
  ): void {
    // 1. Render Developer Counter Window (strictly when Developer is in skill1 STARTUP / ACTIVE)
    snapshot.characters.forEach((character, playerIndex) => {
      const view = views[playerIndex as 0 | 1];
      const isDevSkill1 = character.characterDefinitionId === 'developer'
        && view.pose.state === 'skill1'
        && (view.pose.attackPhase === 'STARTUP' || view.pose.attackPhase === 'ACTIVE');

      if (isDevSkill1) {
        this.renderCounterWindow(graphics, character.position.x, character.position.y, nowMs);
      }
    });

    // 2. Render Floor Traps (Spaghetti Code) strictly from Core debug.boxes
    const trapBoxes = debug.boxes.filter(({ type }) => type === 'TRAP');
    for (const { box } of trapBoxes) {
      this.renderSpaghettiTrap(graphics, box.minX, box.maxX, box.minY, box.maxY, nowMs);
    }

    // 3. Render SLOW status indicators strictly when Core has active SLOW on player
    for (const status of this.activeSlowStatuses) {
      const character = snapshot.characters[status.player];
      if (!character) continue;
      this.renderSlowIndicator(graphics, character.position.x, character.position.y, nowMs);
    }
  }

  public clear(): void {
    this.activeSlowStatuses = [];
  }

  public get isAnyStatusActive(): boolean {
    return this.activeSlowStatuses.length > 0;
  }

  public isSlowActive(player: 0 | 1): boolean {
    return this.activeSlowStatuses.some((s) => s.player === player);
  }

  public isStatusActive(player: 0 | 1, effectType: string): boolean {
    if (effectType === 'SLOW') {
      return this.isSlowActive(player);
    }
    return false;
  }

  private renderCounterWindow(
    g: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    nowMs: number,
  ): void {
    const pulse = 0.8 + 0.2 * Math.sin(nowMs * 0.015);
    const cyan = SKILL_COLORS.developer.primary;
    const white = SKILL_COLORS.developer.accent;

    // Thin cyan ground ring
    g.lineStyle(2, cyan, pulse * 0.85);
    g.strokeEllipse(x, y - 2, 60, 18);

    // Subtle counter bracket motif: { on left, } on right around torso
    const topY = y - 160;
    const midY = y - 100;
    const botY = y - 40;
    const leftX = x - 42;
    const rightX = x + 42;

    // Left bracket {
    g.lineStyle(2.5, cyan, pulse * 0.9);
    g.lineBetween(leftX, topY, leftX + 8, topY);
    g.lineBetween(leftX, topY, leftX, midY - 6);
    g.lineBetween(leftX, midY - 6, leftX - 6, midY);
    g.lineBetween(leftX - 6, midY, leftX, midY + 6);
    g.lineBetween(leftX, midY + 6, leftX, botY);
    g.lineBetween(leftX, botY, leftX + 8, botY);

    // Right bracket }
    g.lineBetween(rightX, topY, rightX - 8, topY);
    g.lineBetween(rightX, topY, rightX, midY - 6);
    g.lineBetween(rightX, midY - 6, rightX + 6, midY);
    g.lineBetween(rightX + 6, midY, rightX, midY + 6);
    g.lineBetween(rightX, midY + 6, rightX, botY);
    g.lineBetween(rightX, botY, rightX - 8, botY);

    // Subtle center node indicator
    g.fillStyle(white, pulse * 0.7);
    g.fillCircle(leftX - 6, midY, 2.5);
    g.fillCircle(rightX + 6, midY, 2.5);
  }

  private renderSpaghettiTrap(
    g: Phaser.GameObjects.Graphics,
    minX: number,
    maxX: number,
    minY: number,
    maxY: number,
    nowMs: number,
  ): void {
    const width = maxX - minX;
    const height = maxY - minY;
    const cx = (minX + maxX) / 2;
    const cyan = SKILL_COLORS.developer.primary;
    const purple = SKILL_COLORS.developer.secondary;
    const darkPurple = SKILL_COLORS.developer.darkPurple;
    const pulse = 0.85 + 0.15 * Math.sin(nowMs * 0.008);

    // Floor hazard boundary (clean wireframe rectangle)
    g.lineStyle(2, darkPurple, 0.6 * pulse);
    g.strokeRect(minX, minY, width, height);

    // Tangled spaghetti code / circuit wires inside the trap area
    g.lineStyle(1.8, cyan, 0.85 * pulse);
    g.lineBetween(minX + 4, maxY - 2, cx - 10, minY + 6);
    g.lineBetween(cx - 10, minY + 6, cx + 12, maxY - 4);
    g.lineBetween(cx + 12, maxY - 4, maxX - 6, minY + 8);

    g.lineStyle(1.8, purple, 0.85 * pulse);
    g.lineBetween(minX + 8, minY + 4, cx + 6, maxY - 6);
    g.lineBetween(cx + 6, maxY - 6, cx - 14, minY + 10);
    g.lineBetween(cx - 14, minY + 10, maxX - 4, maxY - 2);

    // Circuit nodes at intersections
    g.fillStyle(cyan, 0.9 * pulse);
    g.fillCircle(cx - 10, minY + 6, 3);
    g.fillCircle(cx + 12, maxY - 4, 3);
    g.fillStyle(purple, 0.9 * pulse);
    g.fillCircle(cx + 6, maxY - 6, 3);
    g.fillCircle(cx - 14, minY + 10, 3);

    // Floor warning tick marks at edges
    g.lineStyle(1.5, cyan, 0.7 * pulse);
    g.lineBetween(minX, maxY, minX, maxY - 6);
    g.lineBetween(maxX, maxY, maxX, maxY - 6);
  }

  private renderSlowIndicator(
    g: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    nowMs: number,
  ): void {
    const pulse = 0.7 + 0.3 * Math.sin(nowMs * 0.012);
    const purple = SKILL_COLORS.developer.secondary;
    const cyan = SKILL_COLORS.developer.primary;
    const floorY = y - 2;

    // Subtle drag lines at character feet
    g.lineStyle(2, purple, pulse * 0.8);
    g.lineBetween(x - 28, floorY, x + 28, floorY);
    g.lineStyle(1.5, cyan, pulse * 0.7);
    g.lineBetween(x - 20, floorY + 3, x + 20, floorY + 3);

    // Small tangled nodes at feet
    g.fillStyle(purple, pulse * 0.8);
    g.fillCircle(x - 18, floorY, 2.5);
    g.fillCircle(x + 18, floorY, 2.5);
  }
}
